import React, { useEffect, useState } from "react";
import {
    Modal,
    Box,
    Button,
    TextField,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    InputAdornment,
    MenuItem,
    Divider,
    IconButton,
    Stack,
    Card,
    CardContent,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import { addMentorsCSV } from "../../../services/adminServices/AdminServices";
import { toast } from "react-toastify";

const MentorUploadModal = ({ open, handleClose, allCOE, fetchData }) => {
    const [selectedCoe, setSelectedCoe] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
    const [errors, setErrors] = useState({ coe: false, file: false });
    const [uploadResults, setUploadResults] = useState(null);
    const [searchError, setSearchError] = useState("");

    useEffect(() => {
        if (!open) resetAllState();
    }, [open]);

    const resetAllState = () => {
        setSelectedCoe("");
        setSelectedFile(null);
        setErrors({ coe: false, file: false });
        setUploadResults(null);
        setSearchError("");
    };

    const handleCoeChange = (event) => {
        setSelectedCoe(event.target.value);
        setErrors((prev) => ({ ...prev, coe: false }));
    };

    const handleFileChange = (event) => {
        if (event.target.files && event.target.files[0]) {
            setSelectedFile(event.target.files[0]);
            setErrors((prev) => ({ ...prev, file: false }));
        }
    };

    const downloadSampleCSV = () => {
        const headers = ["displayName", "email", "mobileNumber"];
        const sampleRows = [
            ["John Doe", "john.doe@example.com", "9876543210"],
            ["Jane Smith", "jane.smith@example.com", "9123456780"]
        ];
        
        let csvContent = "data:text/csv;charset=utf-8,";
        csvContent += headers.join(",") + "\n";
        sampleRows.forEach(row => {
            csvContent += row.join(",") + "\n";
        });

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "mentor_sample.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleSubmit = async () => {
        let newErrors = { coe: false, file: false };

        if (!selectedCoe) newErrors.coe = true;
        if (!selectedFile) newErrors.file = true;

        if (newErrors.coe || newErrors.file) {
            setErrors(newErrors);
            return;
        }

        try {
            const formData = new FormData();
            formData.append("file", selectedFile);
            const response = await addMentorsCSV(selectedCoe, formData);
            
            const responseData = response?.data;
            if (responseData?.code === 207 || (responseData?.failed && responseData.failed.length > 0)) {
                setUploadResults(responseData);
                fetchData();
            } else {
                resetAllState();
                toast.success(responseData?.message || "Mentors uploaded successfully!");
                fetchData();
                handleClose();
            }
        } catch (error) {
            const errorData = error?.response?.data;
            if (error?.response?.status === 207 || errorData?.code === 207 || (errorData?.failed && errorData.failed.length > 0)) {
                setUploadResults(errorData);
                fetchData();
            } else {
                toast.error(errorData?.message || error?.message || "Upload failed.");
            }
        }
    };

    const highlightText = (text, search) => {
        if (!search) return text;
        const parts = String(text).split(new RegExp(`(${search})`, "gi"));
        return parts.map((part, index) =>
            part.toLowerCase() === search.toLowerCase() ? (
                <span key={index} style={{ backgroundColor: "#ffeb3b", padding: "0 2px", borderRadius: "2px" }}>
                    {part}
                </span>
            ) : (
                part
            )
        );
    };

    const filteredErrors = uploadResults?.failed?.filter((err) => {
        const searchLower = searchError.toLowerCase();
        return (
            String(err.row).toLowerCase().includes(searchLower) ||
            (err.email && err.email.toLowerCase().includes(searchLower)) ||
            (err.reason && err.reason.toLowerCase().includes(searchLower))
        );
    }) || [];

    return (
        <Modal open={open} onClose={handleClose} aria-labelledby="modal-title">
            <Box
                sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: uploadResults ? 900 : 600,
                    minHeight: uploadResults ? "50vh" : "auto",
                    maxHeight: "90vh",
                    bgcolor: "background.paper",
                    boxShadow: 24,
                    borderRadius: 3,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                }}
            >
                {/* Modal Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 3, pb: 2, bgcolor: "#f8f9fa" }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <AssignmentIndIcon color="primary" fontSize="large" />
                        <Typography id="modal-title" variant="h6" fontWeight="bold" color="text.primary">
                            {uploadResults ? "Upload Summary" : "Upload Mentor CSV"}
                        </Typography>
                    </Stack>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Divider />

                <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3, overflowY: "auto" }}>
                    {uploadResults ? (
                        <>
                            {/* Summary Cards */}
                            <Stack direction="row" spacing={2} justifyContent="space-between">
                                <Card sx={{ flex: 1, bgcolor: "#f5f5f5", textAlign: "center", boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                                    <CardContent sx={{ p: 1.5, pb: "12px !important" }}>
                                        <Typography variant="body2" color="text.secondary">Total Records</Typography>
                                        <Typography variant="h5" fontWeight="bold">{uploadResults.summary?.total || 0}</Typography>
                                    </CardContent>
                                </Card>
                                <Card sx={{ flex: 1, bgcolor: "#e8f5e9", textAlign: "center", boxShadow: 'none', border: '1px solid #c8e6c9' }}>
                                    <CardContent sx={{ p: 1.5, pb: "12px !important" }}>
                                        <Stack direction="row" justifyContent="center" alignItems="center" spacing={0.5}>
                                            <CheckCircleOutlineIcon color="success" fontSize="small" />
                                            <Typography variant="body2" color="success.main">Success</Typography>
                                        </Stack>
                                        <Typography variant="h5" fontWeight="bold" color="success.main">{uploadResults.summary?.success || 0}</Typography>
                                    </CardContent>
                                </Card>
                                <Card sx={{ flex: 1, bgcolor: "#ffebee", textAlign: "center", boxShadow: 'none', border: '1px solid #ffcdd2' }}>
                                    <CardContent sx={{ p: 1.5, pb: "12px !important" }}>
                                        <Stack direction="row" justifyContent="center" alignItems="center" spacing={0.5}>
                                            <ErrorOutlineIcon color="error" fontSize="small" />
                                            <Typography variant="body2" color="error.main">Failed</Typography>
                                        </Stack>
                                        <Typography variant="h5" fontWeight="bold" color="error.main">{uploadResults.summary?.failed || 0}</Typography>
                                    </CardContent>
                                </Card>
                            </Stack>

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                <TextField
                                    size="small"
                                    placeholder="Search errors..."
                                    value={searchError}
                                    onChange={(e) => setSearchError(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{ width: 250 }}
                                />
                            </Box>
                            
                            {uploadResults.failed && uploadResults.failed.length > 0 && (
                                <TableContainer component={Paper} elevation={0} sx={{ maxHeight: "40vh", overflow: "auto", border: "1px solid #e0e0e0" }}>
                                    <Table stickyHeader size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: 'bold' }}>Row</TableCell>
                                                <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: 'bold' }}>Email</TableCell>
                                                <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: 'bold' }}>Reason</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {filteredErrors.length > 0 ? (
                                                filteredErrors.map((err, idx) => (
                                                    <TableRow key={idx} hover>
                                                        <TableCell>{highlightText(err.row, searchError)}</TableCell>
                                                        <TableCell>{highlightText(err.email || "-", searchError)}</TableCell>
                                                        <TableCell sx={{ color: 'error.main' }}>{highlightText(err.reason, searchError)}</TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                <TableRow>
                                                    <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            No errors found matching "{searchError}"
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                            <Box sx={{ textAlign: "right", mt: 1 }}>
                                <Button 
                                    variant="contained" 
                                    color="primary" 
                                    onClick={() => { resetAllState(); handleClose(); }}
                                    sx={{ borderRadius: 2, textTransform: 'none', px: 4 }}
                                >
                                    Close
                                </Button>
                            </Box>
                        </>
                    ) : (
                        <>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: -2 }}>
                                <Button 
                                    variant="outlined" 
                                    color="primary" 
                                    size="small"
                                    startIcon={<DownloadIcon />} 
                                    onClick={downloadSampleCSV}
                                    sx={{ textTransform: 'none', borderRadius: 2 }}
                                >
                                    Sample CSV
                                </Button>
                            </Box>

                            <TextField
                                select
                                fullWidth
                                label="Select COE *"
                                value={selectedCoe}
                                onChange={handleCoeChange}
                                error={errors.coe}
                                helperText={errors.coe ? "Please select a COE*" : ""}
                                variant="outlined"
                                sx={{ mt: 1 }}
                            >
                                {allCOE?.map((coe) => (
                                    <MenuItem key={coe.id} value={coe.id}>
                                        {coe.name}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <Box>
                                <Button
                                    component="label"
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        textTransform: "none",
                                        borderRadius: 2,
                                        width: '100%',
                                        minHeight: 140,
                                        border: errors.file ? "2px dashed #d32f2f" : "2px dashed #90caf9",
                                        bgcolor: errors.file ? "#ffebee" : "#f3f8ff",
                                        color: errors.file ? "#d32f2f" : "text.secondary",
                                        transition: "all 0.2s ease-in-out",
                                        "&:hover": {
                                            bgcolor: "#e3f2fd",
                                            borderColor: "#42a5f5",
                                        }
                                    }}
                                >
                                    <CloudUploadIcon sx={{ fontSize: 48, mb: 1, color: errors.file ? "error.main" : "primary.main" }} />
                                    <Typography variant="body1" fontWeight="medium" color={errors.file ? "error.main" : "text.primary"}>
                                        {selectedFile ? selectedFile.name : "Click to select a CSV file"}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                        {selectedFile ? "File selected" : "Must be a valid .csv file"}
                                    </Typography>
                                    <input type="file" hidden accept=".csv" onChange={handleFileChange} />
                                </Button>
                                {errors.file && <Typography color="error" variant="caption" sx={{ mt: 0.5, display: 'block', ml: 1 }}>Please select a CSV file*</Typography>}
                            </Box>

                            <Button 
                                variant="contained" 
                                color="primary" 
                                fullWidth 
                                onClick={handleSubmit}
                                sx={{ py: 1.2, fontSize: "16px", borderRadius: 2, mt: 1 }}
                            >
                                Upload Mentors
                            </Button>
                        </>
                    )}
                </Box>
            </Box>
        </Modal>
    );
};

export default MentorUploadModal;
